import { EmployeeColumnFilterValue } from "@/pages/employees/index/EmployeeColumnFilterValue.tsx";
import {
  EmployeeColumnFilterRenderers,
  resolveEmployeeColumnFilterRenderer,
} from "@/pages/employees/index/employee-column-filter-renderers.ts";
import {
  applyColumnFilterCondition,
  ColumnFilterValue,
  EmployeeColumnFilterCondition,
  getColumnFilterCondition,
  getColumnFilterConditions,
} from "@/pages/employees/index/employee-column-filter-conditions.ts";
import {
  ApiEmployeeColumn,
  ApiEmployeeColumnFilter,
  ApiEmployeeColumnFilterType,
} from "@/types/employees/employee-column.types.ts";
import { Select, SelectItem, Stack } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface EmployeeColumnFilterFieldProps {
  column: ApiEmployeeColumn;
  filter: ApiEmployeeColumnFilter;
  onChange: (value: ColumnFilterValue) => void;
  renderers?: EmployeeColumnFilterRenderers;
}

/**
 * Поле фильтра по колонке каталога — единственная точка рендера фильтров
 * панели: подпись колонки, условие и контрол значения. Свой контрол значения
 * подставляется через `renderers` (по ключу колонки или по `filter_type`),
 * остальные рисует `EmployeeColumnFilterValue`.
 */
export function EmployeeColumnFilterField({
  column,
  filter,
  onChange,
  renderers,
}: EmployeeColumnFilterFieldProps) {
  const { t } = useTranslation();
  const condition = getColumnFilterCondition(column, filter);
  const renderValue = resolveEmployeeColumnFilterRenderer(column, renderers);

  const conditionOptions = useMemo<SelectItem[]>(() => {
    const valueTitleByType: Partial<Record<ApiEmployeeColumnFilterType, string>> = {
      [ApiEmployeeColumnFilterType.text]: t("core:column_filter_contains"),
      [ApiEmployeeColumnFilterType.number]: t("core:column_filter_in_range"),
      [ApiEmployeeColumnFilterType.date]: t("core:column_filter_in_period"),
      [ApiEmployeeColumnFilterType.select]: t("core:column_filter_any_of"),
      [ApiEmployeeColumnFilterType.employee]: t("core:column_filter_any_of"),
    };
    const titles: Record<EmployeeColumnFilterCondition, string> = {
      [EmployeeColumnFilterCondition.value]: valueTitleByType[column.filter_type] ?? "",
      [EmployeeColumnFilterCondition.empty]: t("core:column_filter_is_empty"),
      [EmployeeColumnFilterCondition.filled]: t("core:column_filter_is_filled"),
      [EmployeeColumnFilterCondition.yes]: t("common:yes"),
      [EmployeeColumnFilterCondition.no]: t("common:no"),
    };
    const conditions = getColumnFilterConditions(column.filter_type);
    // Сохранённое is_empty показываем, чтобы действующее условие не было скрыто.
    // В новых фильтрах эти условия больше не предлагаются.
    const visibleConditions =
      condition && !conditions.includes(condition) ? [condition, ...conditions] : conditions;
    return visibleConditions.map((item) => ({
      id: item,
      title: titles[item],
    }));
  }, [column.filter_type, condition, t]);

  return (
    <Stack>
      <div className="grid w-full grid-cols-1 gap-sm [&>*]:min-w-0 [&>*]:w-full">
        {conditionOptions.length > 1 && (
          <Select
            label={column.title}
            value={condition ?? null}
            options={conditionOptions}
            emptyTitle={t("common:select")}
            onChange={(value) => {
              if (value == null || value === condition) return;
              onChange(applyColumnFilterCondition(value as EmployeeColumnFilterCondition));
            }}
          />
        )}
        {condition === EmployeeColumnFilterCondition.value &&
          (renderValue ? (
            renderValue({ column, filter, onChange })
          ) : (
            <EmployeeColumnFilterValue column={column} filter={filter} onChange={onChange} />
          ))}
      </div>
    </Stack>
  );
}
